import type Ajv from "ajv";
import type { CatalogFieldState } from "@/lib/api/generated/casedb";
import type { AiReviewStatus, FieldTally, FollowupReviewCounts, ReviewCounts, ReviewDecision } from "../model/caseReview";
import { REVIEW_DECISIONS } from "../constants/caseReviewDecisions";
import { requiredFieldsIn, type ReviewForm } from "./caseReviewOutstanding";

/**
 * The counts every scope of Case Review shows — a record, a subsection, a section, the case.
 *
 * <p>One field is tallied once (`tallyForm`), a scope counts the tallies of its fields
 * (`countTallies`), and a parent is only ever the sum of its children (`sumReviewCounts`). Nothing
 * reads the DOM, and nothing here knows which section it is counting.
 */

export const EMPTY_REVIEW_COUNTS: ReviewCounts = {
  ai: { total: 0, accepted: 0, corrected: 0, rejected: 0, manual: 0 },
  required: { total: 0, missing: [] },
  unsaved: 0,
};

export const aiReviewed = (counts: ReviewCounts): number => counts.ai.accepted + counts.ai.corrected + counts.ai.rejected;
export const aiToReview = (counts: ReviewCounts): number => counts.ai.total - aiReviewed(counts);
export const requiredLeft = (counts: ReviewCounts): number => counts.required.missing.length;
export const requiredFilled = (counts: ReviewCounts): number => counts.required.total - requiredLeft(counts);

/** The AI review states that are not a reviewer's decision. */
export const AI_REVIEW_STATUS = { none: "none", pending: "pending", manual: "manual" } as const;

/**
 * A field's AI review: none without a review record, otherwise its decision; undecided, it is
 * pending when the extraction is usable or Theragenie proposed a value, and manual otherwise.
 *
 * <p>The one definition every count, filter and flag uses, so they cannot disagree.
 */
export function aiStatusOf(state: CatalogFieldState | undefined, decision: ReviewDecision | undefined,
                           hasProposal = false): AiReviewStatus {
  if (!state || state.review_id == null) return AI_REVIEW_STATUS.none;
  if (decision) return decision;
  return state.normalized_extracted_value_available === false && !hasProposal
    ? AI_REVIEW_STATUS.manual : AI_REVIEW_STATUS.pending;
}

/** Flagged: pending, undecided, extraction unusable and no proposal — the reviewer must type it. */
export const needsManualEntry = (state: CatalogFieldState | undefined, decision: ReviewDecision | undefined,
                                 hasProposal: boolean): boolean =>
  aiStatusOf(state, decision, hasProposal) === AI_REVIEW_STATUS.manual;

/**
 * Every field of one form that matters to a count: the ones with an AI review record and the
 * ones a rule requires right now. Editability is not applied (a locked field is still work left).
 */
export function tallyForm(form: ReviewForm, ajv: Ajv): FieldTally[] {
  const byKey = new Map<string, FieldTally>();
  const entry = (key: string, fieldId: string): FieldTally => {
    let tally = byKey.get(key);
    if (!tally) {
      tally = { key, fieldId, ai: "none", required: false, filled: false };
      if (form.recordId !== undefined) tally.recordId = form.recordId;
      byKey.set(key, tally);
    }
    return tally;
  };
  for (const [fieldId, state] of Object.entries(form.states)) {
    const key = form.keyOf(fieldId);
    if (key) entry(key, fieldId).ai = aiStatusOf(state, form.decisions[key], !!form.hasProposal?.(fieldId));
  }
  for (const field of requiredFieldsIn(form, ajv)) {
    const tally = entry(field.key, field.fieldId);
    tally.required = true;
    tally.filled = field.filled;
  }
  return [...byKey.values()];
}

/** One scope's counts from the tallies of its fields; `keys` restricts to a subsection's fields. */
export function countTallies(tallies: FieldTally[], keys?: Set<string>): ReviewCounts {
  const counts: ReviewCounts = { ai: { total: 0, accepted: 0, corrected: 0, rejected: 0, manual: 0 }, required: { total: 0, missing: [] }, unsaved: 0 };
  for (const tally of tallies) {
    if (keys && !keys.has(tally.key)) continue;
    if (tally.ai === AI_REVIEW_STATUS.manual) {
      counts.ai.manual += 1;
    } else if (tally.ai !== AI_REVIEW_STATUS.none) {
      counts.ai.total += 1;
      if (tally.ai === REVIEW_DECISIONS.accepted) counts.ai.accepted += 1;
      if (tally.ai === REVIEW_DECISIONS.corrected) counts.ai.corrected += 1;
      if (tally.ai === REVIEW_DECISIONS.rejected) counts.ai.rejected += 1;
    }
    if (tally.required) {
      counts.required.total += 1;
      if (!tally.filled) {
        counts.required.missing.push(tally.recordId === undefined
          ? { fieldId: tally.fieldId } : { fieldId: tally.fieldId, recordId: tally.recordId });
      }
    }
  }
  return counts;
}

/** Two scopes' follow-up work together; either may have none. */
export function addFollowupCounts(left: FollowupReviewCounts | undefined, right: FollowupReviewCounts | undefined): FollowupReviewCounts | undefined {
  if (!left || !right) return left ?? right;
  return {
    total: left.total + right.total,
    decided: left.decided + right.decided,
    records: { total: left.records.total + right.records.total, resolved: left.records.resolved + right.records.resolved },
  };
}

/** Whether a scope has any follow-up work at all: a proposal to decide or a record to answer. */
export const hasFollowupWork = (followup: FollowupReviewCounts | undefined): followup is FollowupReviewCounts =>
  !!followup && (followup.total > 0 || followup.records.total > 0);

/** A parent's counts: the sum of its children's, gaps kept in order. */
export function sumReviewCounts(children: ReviewCounts[]): ReviewCounts {
  const sum: ReviewCounts = { ai: { total: 0, accepted: 0, corrected: 0, rejected: 0, manual: 0 }, required: { total: 0, missing: [] }, unsaved: 0 };
  let followup: ReviewCounts["followup"];
  for (const child of children) {
    sum.ai.total += child.ai.total;
    sum.ai.accepted += child.ai.accepted;
    sum.ai.corrected += child.ai.corrected;
    sum.ai.rejected += child.ai.rejected;
    sum.ai.manual += child.ai.manual;
    sum.required.total += child.required.total;
    sum.required.missing.push(...child.required.missing);
    sum.unsaved += child.unsaved;
    followup = addFollowupCounts(followup, child.followup);
  }
  return followup ? { ...sum, followup } : sum;
}

/** A record form's tally key: its collection prefix and its id (a child can share its parent's id). */
export const recordTallyKey = (collectionPrefix: string, recordId: string): string => `${collectionPrefix}|${recordId}`;

/** The key `tallyForm`'s result is stored under: the section for a scalar form, else the record's. */
export function formTallyKey(form: Pick<ReviewForm, "sectionId" | "recordId" | "collectionPrefix">): string {
  return form.recordId !== undefined && form.collectionPrefix
    ? recordTallyKey(form.collectionPrefix, form.recordId) : form.sectionId;
}

/** The same counts with how many of the scope's fields have unsaved changes. */
export function withUnsaved(counts: ReviewCounts, unsaved: number): ReviewCounts {
  return { ...counts, unsaved };
}

/** What a collection needs to be counted; the review hook's collection api satisfies it. */
export interface CountedCollection {
  spec?: { children?: Array<{ collectionId: string }> };
  rows: Array<{ recordId: string }>;
  tallies: (recordId: string) => FieldTally[];
  dirty: (recordId: string) => string[];
  child?: (recordId: string, collectionId: string) => CountedCollection | undefined;
}

/**
 * A collection's counts, one entry per record and per nested record.
 *
 * <p>Without `keys`, every field of every record, children included — a dose is a field the
 * reviewer has to look at whether it sits on the product or on the regimen inside it. With `keys`
 * (a section whose tabs are its record's groups), only that group's own fields and the child
 * collections the group holds: the collection's total would put one number on every tab.
 */
export function collectionCounts(api: CountedCollection, keys?: Set<string>): ReviewCounts[] {
  const children = (api.spec?.children ?? []).filter((child) => !keys || keys.has(child.collectionId));
  const ownKeys = keys && new Set([...keys].filter((key) => !children.some((child) => child.collectionId === key)));
  return api.rows.flatMap((row) => {
    const dirty = api.dirty(row.recordId).filter((key) => !ownKeys || ownKeys.has(key));
    return [
      withUnsaved(countTallies(api.tallies(row.recordId), ownKeys), dirty.length),
      ...children.flatMap((child) => {
        const nested = api.child?.(row.recordId, child.collectionId);
        return nested ? collectionCounts(nested) : [];
      }),
    ];
  });
}
